import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    motion,
    useReducedMotion,
    useScroll,
    useTransform,
} from "framer-motion";
import { ArrowRight, CreditCard, Truck, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EASE } from "@/components/motion/Reveal";

// Fotografía de Pexels (licencia libre, uso comercial sin atribución).
const HERO_IMG =
    "https://images.pexels.com/photos/7857553/pexels-photo-7857553.jpeg?auto=compress&cs=tinysrgb&w=1920";
const HERO_IMG_SM =
    "https://images.pexels.com/photos/7857553/pexels-photo-7857553.jpeg?auto=compress&cs=tinysrgb&w=1080";

export default function Hero() {
    const reduce = useReducedMotion();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const sectionRef = useRef(null);

    // La foto se queda atrás mientras la página sube sobre ella. Es lo que
    // explica que el panel del producto aparezca encima del hero.
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"],
    });
    const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

    // El correo solo precarga el registro; la validación real vive en /registro.
    const submit = (e) => {
        e.preventDefault();
        const value = email.trim();
        navigate(
            value
                ? `/registro?rol=merchant&email=${encodeURIComponent(value)}`
                : "/registro?rol=merchant"
        );
    };

    // Cada línea del titular se descubre tras su propia máscara.
    const Line = ({ children, delay, className = "" }) => (
        <span className="block overflow-hidden pb-[0.08em]">
            {reduce ? (
                <span className={`block ${className}`}>{children}</span>
            ) : (
                <motion.span
                    className={`block ${className}`}
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay, ease: EASE }}
                >
                    {children}
                </motion.span>
            )}
        </span>
    );

    const fadeUp = (delay) =>
        reduce
            ? {}
            : {
                  initial: { opacity: 0, y: 20 },
                  animate: { opacity: 1, y: 0 },
                  transition: { duration: 0.7, delay, ease: EASE },
              };

    return (
        <section
            ref={sectionRef}
            data-testid="home-hero"
            className="relative isolate overflow-hidden bg-[#1A1A1A]"
        >
            {/* Fotografía */}
            <motion.div
                aria-hidden="true"
                style={reduce ? undefined : { y: imageY }}
                initial={reduce ? false : { scale: 1.08 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.2, ease: EASE }}
                className="absolute inset-0 -z-10 will-change-transform"
            >
                <img
                    src={HERO_IMG}
                    srcSet={`${HERO_IMG_SM} 1080w, ${HERO_IMG} 1920w`}
                    sizes="100vw"
                    alt=""
                    fetchpriority="high"
                    decoding="async"
                    className="w-full h-[112%] object-cover object-center"
                />
            </motion.div>

            {/* Velo: garantiza el contraste del texto sobre cualquier zona de la
                foto. Va en estilo en línea a propósito. Ojo con reescribirlo
                como clases: el modificador de opacidad suelto de Tailwind solo
                acepta múltiplos de 5 (bg-black/85 sí, /82 no — esa clase se
                descarta en silencio y el velo desaparece). Además un degradado
                de cuatro paradas no se expresa con from/via/to. */}
            <div
                aria-hidden="true"
                className="absolute inset-0 -z-10"
                style={{
                    background:
                        "linear-gradient(180deg, rgba(11,11,14,0.74) 0%, rgba(11,11,14,0.66) 42%, rgba(11,11,14,0.70) 70%, rgba(11,11,14,0.36) 100%)",
                }}
            />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-44 sm:pt-32 sm:pb-52 lg:pt-40 lg:pb-64">
                <div className="max-w-3xl mx-auto text-center">
                    <h1
                        className="text-[2.75rem] sm:text-6xl lg:text-[5rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white"
                        style={{
                            textShadow:
                                "0 1px 1px rgba(0,0,0,0.5), 0 7px 10px rgba(0,0,0,0.55)",
                        }}
                    >
                        <Line delay={0.15}>Vende en línea.</Line>
                        <Line delay={0.28} className="text-indigo-300">
                            Sin renta mensual.
                        </Line>
                    </h1>

                    <motion.p
                        {...fadeUp(0.5)}
                        style={{ textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}
                        className="mt-6 text-lg sm:text-xl text-white/85 leading-relaxed max-w-2xl mx-auto"
                    >
                        Crea tu tienda en línea, cobra con tarjeta y envía a
                        todo México desde un solo panel. Sin mensualidades y
                        sin comisión por venta.
                    </motion.p>

                    {/* Captura de correo → registro precargado */}
                    <motion.form
                        {...fadeUp(0.6)}
                        onSubmit={submit}
                        data-testid="hero-signup-form"
                        className="mt-10 flex flex-col sm:flex-row gap-3 max-w-xl mx-auto"
                    >
                        <label htmlFor="hero-email" className="sr-only">
                            Correo electrónico
                        </label>
                        <input
                            id="hero-email"
                            name="email"
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            data-testid="hero-email-input"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="tucorreo@ejemplo.com"
                            className="flex-1 h-14 px-5 rounded-xl bg-white text-base text-[#1A1A1A] placeholder:text-zinc-500 shadow-lg shadow-black/20 transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                        />
                        <Button
                            type="submit"
                            data-testid="hero-cta-create-store"
                            className="group h-14 px-8 rounded-full text-base font-semibold bg-primary hover:bg-primary-hover text-primary-foreground shrink-0 shadow-lg shadow-black/25 transition-transform active:scale-[0.98]"
                        >
                            Crear mi tienda
                            <ArrowRight
                                className="w-4 h-4 ml-1.5 transition-transform duration-200 group-hover:translate-x-0.5"
                                aria-hidden="true"
                            />
                        </Button>
                    </motion.form>

                    <motion.p
                        {...fadeUp(0.68)}
                        style={{ textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}
                        className="mt-4 text-sm text-white/80"
                    >
                        Gratis para siempre. Sin tarjeta de crédito.
                    </motion.p>

                    {/* Tres promesas — verificables, sin métricas inventadas */}
                    <motion.ul
                        {...fadeUp(0.76)}
                        style={{ textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}
                        className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-white/85"
                    >
                        {[
                            [Store, "Tu tienda lista hoy"],
                            [CreditCard, "Pagos con tarjeta"],
                            [Truck, "Envíos a todo México"],
                        ].map(([Icon, label]) => (
                            <li key={label} className="flex items-center gap-2">
                                <Icon className="w-4 h-4 text-indigo-300" aria-hidden="true" />
                                {label}
                            </li>
                        ))}
                    </motion.ul>
                </div>
            </div>
        </section>
    );
}
